import {
  createContext,
  lazy,
  Suspense,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
  type ReactNode,
} from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import {
  Sidebar,
  Topbar,
  PageCrumbsProvider,
  screenLabel,
  seesMoney,
  type ScreenKey,
} from './components/shell';
import { ErrorBoundary, ScreenErrorFallback } from './components/ErrorBoundary';
import { SmartTimerPill } from './components/timer';
import { canTimeProjects } from './lib/projectTimer';
import { InlineStatus, ToastProvider } from './components/feedback';
import { Dialog } from './components/dialog';
import { Placeholder } from './screens/Placeholder';
import { WorkspaceScreen } from './screens/WorkspaceScreen';
import { TalentScreen } from './screens/TalentScreen';
import { MeScreen } from './screens/MeScreen';
import { SecurityScreen } from './screens/SecurityScreen';
import { ConsentScreen } from './screens/ConsentScreen';
import { WhatsNewScreen } from './screens/WhatsNewScreen';
import { LoginScreen, OtpScreen, ResetPasswordScreen } from './screens/LoginScreen';
import { WelcomeScreen } from './screens/WelcomeScreen';
import { DashboardScreen } from './screens/DashboardScreen';
import { ProjectsScreen } from './screens/ProjectsScreen';
import { ReportsScreen } from './screens/ReportsScreen';
import { CapacityScreen } from './screens/CapacityScreen';
import { TimesheetsScreen } from './screens/TimesheetsScreen';
import { GanttScreen } from './screens/GanttScreen';
import { PeopleHRScreen } from './screens/PeopleHRScreen';
import { OrgSettingsScreen } from './screens/OrgSettingsScreen';
import { useAuth } from './lib/auth';
import {
  can,
  canOpen,
  isModuleEnabled,
  WORKSPACE_MODULES,
  type ModuleGate,
  type PermMap,
} from './lib/roles';
import { useAttention } from './lib/attention';
import { useBelow } from './lib/useMediaQuery';

// A project's page carries the board, the editor and their libraries: its own
// chunk, so they never weigh on the first load (spec §7).
const ProjectDetailScreen = lazy(() =>
  import('./screens/ProjectDetailScreen').then((m) => ({ default: m.ProjectDetailScreen })),
);
// Owner and admin only: its chunk stays off everyone else's first load.
const ProfitabilityScreen = lazy(() =>
  import('./screens/ProfitabilityScreen').then((m) => ({ default: m.ProfitabilityScreen })),
);
const RouteSpinner = () => (
  <div role="status" style={{ padding: 40, color: 'var(--hf-ink-3)' }}>
    Loading…
  </div>
);

type Tweaks = { theme: 'dark' | 'light'; sidebar: 'wide' | 'icon' };
const DEFAULT_TWEAKS: Tweaks = { theme: 'dark', sidebar: 'wide' };

const SCREEN_KEYS: ScreenKey[] = [
  'dashboard',
  'projects',
  'project',
  'profitability',
  'pipeline',
  'capacity',
  'talent',
  'timesheets',
  'gantt',
  'forecasting',
  'workspace',
  'hr',
  'contracts',
  'reports',
  'org',
  'me',
  'security',
  'whats-new',
];

// Modules still on the roadmap — clean "coming next" stubs so every nav item is reachable.
const PLACEHOLDERS: Record<string, { title: string; phase: string; blurb: string }> = {
  pipeline: {
    title: 'Pipeline',
    phase: 'Next',
    blurb: 'The deal/stage board — opportunities moving from intake to kickoff.',
  },
  forecasting: {
    title: 'Forecasting',
    phase: 'Next',
    blurb: 'Predictive capacity and revenue — where the studio is headed next quarter.',
  },
  // `inbox` is deliberately absent — it is Workspace's Notifications tab now.
  // PLACEHOLDERS drives the catch-all route map below, so leaving it here would
  // register a second /inbox route and shadow the redirect.
  contracts: {
    title: 'Contracts',
    phase: 'Next',
    blurb: 'Templates and signed documents — generation, versioning, and the audit trail.',
  },
};

// Route path → the permission module that gates it (defaults to the path itself).
const ROUTE_MODULE: Record<string, string> = {
  project: 'projects',
  gantt: 'schedule',
  hr: 'people',
};

export const pathToScreen = (pathname: string): ScreenKey => {
  const key = pathname.replace(/^\//, '') as ScreenKey;
  return SCREEN_KEYS.includes(key) ? key : 'dashboard';
};

// Land each role on the first enabled module it can actually access.
const HOME_ORDER: { module: ModuleGate; path: string }[] = [
  { module: 'people', path: '/hr' },
  { module: 'timesheets', path: '/timesheets' },
  { module: WORKSPACE_MODULES, path: '/workspace' },
  { module: 'org', path: '/org' },
];
const homeFor = (perms: PermMap): string =>
  HOME_ORDER.find((h) => canOpen(perms, h.module))?.path ?? '/me';

// Why a guarded route sent you somewhere else. A bounce used to say nothing: a
// member opening /org landed on Timesheets, and a link into a module they
// lacked looked like a dead button (SH-11).
//
// Held in AppBody's state, not in the redirect's history entry. People, home
// for every role with people access, replaces its own entry on mount to add
// ?person=, which dropped the state and the notice with it; and where the entry
// survived, the notice came back on reload and on Back.
type Denied = {
  /** The screen that refused. */
  denied: string;
  off: boolean;
  /** Where the redirect lands: the notice shows there, and nowhere after. */
  at: string;
  /** What was asked for: once the viewer can open it, the notice is untrue. */
  module: ModuleGate;
  also?: (perms: PermMap) => boolean;
};
const opens = (perms: PermMap, d: Pick<Denied, 'module' | 'also'>) =>
  canOpen(perms, d.module) && (d.also?.(perms) ?? true);
const DeniedContext = createContext<(d: Denied) => void>(() => {});

const Guard = ({
  perms,
  module,
  also,
  label,
  children,
}: {
  perms: PermMap;
  module: ModuleGate;
  /** A further check on top of `module` (shell's NavItem.also, the same one). */
  also?: (perms: PermMap) => boolean;
  /** The name to refuse by, where the route's title is another screen's
   *  (no route needs it today; the path names the screen). */
  label?: string;
  children: ReactNode;
}) => {
  const { pathname } = useLocation();
  const report = useContext(DeniedContext);
  const ok = opens(perms, { module, also });
  const home = homeFor(perms);
  const denied = label ?? screenLabel(pathToScreen(pathname));
  // Switched off in this build (lib/roles ENABLED_MODULES) is not a missing
  // permission, and no admin can grant it.
  const off = ![module].flat().some(isModuleEnabled);
  useEffect(() => {
    if (!ok) report({ denied, off, at: home, module, also });
  }, [ok, report, denied, off, home, module, also]);
  if (ok) return <>{children}</>;
  return <Navigate to={home} replace />;
};

// A status with a name, so the first thing a screen reader meets is not an
// unlabelled spinner (SH-19).
const Splash = () => (
  <div
    role="status"
    aria-label="Loading Hyprflow"
    style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--hf-paper)',
      position: 'relative',
      overflow: 'hidden',
    }}
  >
    <div className="hf-atmosphere" />
    <span
      style={{
        position: 'relative',
        zIndex: 1,
        width: 22,
        height: 22,
        borderRadius: 999,
        border: '2px solid var(--hf-rule-3)',
        borderTopColor: 'var(--hf-accent)',
        display: 'inline-block',
        animation: 'hf-spin 0.7s linear infinite',
      }}
    />
  </div>
);

// The one Toast host (components/feedback.tsx), around every screen the app
// renders — the sign-in and intake screens as well as the shell.
export default function App() {
  return (
    <ToastProvider>
      <AppBody />
    </ToastProvider>
  );
}

// sessionStorage: the tablet's Expand, for this browser session only.
const TABLET_SIDEBAR = 'hf.tabletSidebar';

function AppBody() {
  const location = useLocation();
  const screen = pathToScreen(location.pathname);
  // Cleared when the path changes to anywhere but the landing screen. During
  // render, off the previous path, not in an effect: the redirect is a
  // transition, so the denial is set a render before the path moves, and only
  // a real change of path may clear it.
  const { session, loading, perms, roleId, profile, signOut, otpEmail, recovery } = useAuth();
  const [denied, setDenied] = useState<Denied | null>(null);
  // The shell by width (BREAKPOINTS in lib/useMediaQuery.ts; UI-CONVENTIONS §8).
  // From lg (1024) the sidebar the person chose, wide or the rail. From md to lg
  // the rail, which they may widen for the session without changing their
  // desktop choice. Below md no sidebar at all — 224px of one left a 390px phone
  // 166px of page (SH-01) — but a Menu drawer, and the day clock at the bottom.
  const phone = useBelow('md');
  const tablet = useBelow('lg') && !phone;
  // Expand on a tablet lasts the browser session (sessionStorage), and leaves
  // the desktop choice in hf.tweaks alone. On a touch tablet the rail's names
  // are tooltips a finger can't hover, so one Expand must outlive a reload.
  const [tabletWide, setTabletWide] = useState(() => {
    try {
      return sessionStorage.getItem(TABLET_SIDEBAR) === 'wide';
    } catch {
      return false;
    }
  });
  const toggleTablet = () => {
    setTabletWide(!tabletWide);
    try {
      sessionStorage.setItem(TABLET_SIDEBAR, tabletWide ? 'icon' : 'wide');
    } catch {
      /* ignore */
    }
  };
  // The drawer shuts on any navigation (a link in it, Back) and when the width
  // grows past md, where the sidebar is back. The Menu button that opened it
  // goes in that same commit, so the drawer's focus return would land on
  // <body>: #main takes it instead. Counted, so each such close refocuses.
  const [menuOpen, setMenuOpen] = useState(false);
  const [widthShut, setWidthShut] = useState(0);
  if (menuOpen && !phone) {
    setMenuOpen(false);
    setWidthShut((n) => n + 1);
  }
  useLayoutEffect(() => {
    if (widthShut) document.getElementById('main')?.focus();
  }, [widthShut]);
  const [path, setPath] = useState(location.pathname);
  if (path !== location.pathname) {
    setPath(location.pathname);
    if (denied && denied.at !== location.pathname) setDenied(null);
    setMenuOpen(false);
  }
  // Nor does it outlive the session it was raised for. This component stays
  // mounted across sign-out, so the next person to sign in on the same tab was
  // told they had no access to a screen someone else had asked for.
  const uid = session?.user.id;
  const [who, setWho] = useState(uid);
  if (who !== uid) {
    setWho(uid);
    setDenied(null);
  }

  const [tweaks, setTweaks] = useState<Tweaks>(() => {
    try {
      return { ...DEFAULT_TWEAKS, ...JSON.parse(localStorage.getItem('hf.tweaks') || '{}') };
    } catch {
      return DEFAULT_TWEAKS;
    }
  });

  useEffect(() => {
    document.documentElement.dataset.theme = tweaks.theme;
  }, [tweaks.theme]);

  // Every attention count in the app, from one registry (lib/attention.ts), so
  // the sidebar and the tab inside always show the same number. The bug fan-out
  // trigger deliberately skips the reporter, so an admin filing their own report
  // gets no bell notification and — before this — no signal whatsoever that
  // anything landed. head+count sends no rows over the wire, and re-running on
  // navigation is frequent enough that it needs no realtime subscription.
  const attention = useAttention(perms, session?.user.id, screen);

  const updateTweak = <K extends keyof Tweaks>(k: K, v: Tweaks[K]) => {
    const next = { ...tweaks, [k]: v };
    setTweaks(next);
    try {
      localStorage.setItem('hf.tweaks', JSON.stringify(next));
    } catch {
      /* ignore */
    }
  };

  if (recovery) return <ResetPasswordScreen />;
  if (otpEmail) return <OtpScreen email={otpEmail} />;
  if (loading) return <Splash />;
  if (!session) return <LoginScreen />;
  // An invited employee lands here with a session but no password (an invite link
  // fires SIGNED_IN, not PASSWORD_RECOVERY), so they must pass through intake
  // before the workspace or they can never sign in again. The flag is set by the
  // invite-user function and cleared when they finish. Existing employees have no
  // such key, so it is undefined for them and nobody is gated retroactively — no
  // backfill, unlike a `profiles.onboarded_at is null` test would have needed.
  // Last of the four gates: it needs session.user for RLS-scoped writes, and the
  // form prefills from `profile`, which is only loaded once `loading` clears.
  if (session.user.user_metadata?.onboard) return <WelcomeScreen />;
  // Connecting an MCP app (Claude, ChatGPT) is a step of sign-in, not a page of
  // the workspace: GoTrue sends the person here, and they leave for the app.
  if (location.pathname === '/oauth/consent') return <ConsentScreen />;

  const sidebar = {
    currentScreen: screen,
    perms,
    roleId,
    userName: profile?.full_name ?? null,
    userTitle: profile?.title ?? null,
    theme: tweaks.theme,
    onToggleTheme: () => updateTweak('theme', tweaks.theme === 'dark' ? 'light' : 'dark'),
    onSignOut: signOut,
    attention: attention.byModule,
  };
  // Not the day clock on Timesheets: its day-clock card is the same control, and
  // two Check in buttons on one screen read as two different things (SH-20).
  // The project timer has no card there, so it stays.
  const onTimesheets = screen === 'timesheets';
  const clock =
    can(perms, 'timesheets') && (!onTimesheets || canTimeProjects(perms)) ? (
      <SmartTimerPill dayClock={!onTimesheets} />
    ) : undefined;

  return (
    <div
      style={{
        display: 'flex',
        // dvh: on a phone 100vh is the height with the browser's bars hidden, so
        // the bottom of the shell (the day clock) sat under them.
        height: '100dvh',
        position: 'relative',
        overflow: 'hidden',
        background: 'var(--hf-paper)',
      }}
    >
      {/* First in the tab order: past the sidebar and the topbar, straight to the
          screen (SH-09). Focus moves without a #main landing in the address. */}
      <a
        href="#main"
        className="hf-skip"
        onClick={(e) => {
          e.preventDefault();
          document.getElementById('main')?.focus();
        }}
      >
        Skip to content
      </a>
      <div className="hf-atmosphere" />
      {/* viewport-fit=cover (index.html) draws the page under a notch and the
          home indicator; the shell keeps its content out of them. All 0 on a
          desktop. The day-clock bar clears the bottom one itself. */}
      <div
        style={{
          display: 'flex',
          width: '100%',
          height: '100%',
          position: 'relative',
          zIndex: 1,
          padding:
            'env(safe-area-inset-top) env(safe-area-inset-right) 0 env(safe-area-inset-left)',
        }}
      >
        {phone ? (
          <Dialog open={menuOpen} onClose={() => setMenuOpen(false)} title="Menu" placement="start">
            <Sidebar {...sidebar} mode="drawer" onNavigate={() => setMenuOpen(false)} />
          </Dialog>
        ) : (
          <Sidebar
            {...sidebar}
            mode={tablet ? (tabletWide ? 'wide' : 'icon') : tweaks.sidebar}
            onToggleSidebar={
              tablet
                ? toggleTablet
                : () => updateTweak('sidebar', tweaks.sidebar === 'wide' ? 'icon' : 'wide')
            }
          />
        )}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {/* Wraps the Topbar (reads crumbs) and the routed screens (publish
              them via usePageCrumbs), so the two can talk without prop-drilling
              through every route. */}
          <PageCrumbsProvider>
            <Topbar
              currentScreen={screen}
              unread={attention.byQueue.notifications}
              onUnreadChange={attention.reload}
              compact={phone}
              onMenu={() => setMenuOpen(true)}
              menuOpen={menuOpen}
              right={phone ? undefined : clock}
            />
            <main
              id="main"
              tabIndex={-1}
              className="hf-scroll"
              style={{ flex: 1, overflow: 'auto' }}
            >
              {/* Not once it can be opened: a grant that lands while the notice
                  is up makes it untrue. */}
              {denied?.at === location.pathname && !opens(perms, denied) && (
                <div style={{ padding: 'var(--hf-space-4) var(--hf-space-5) 0' }}>
                  <InlineStatus
                    tone="info"
                    action={{ label: 'Dismiss', onClick: () => setDenied(null) }}
                  >
                    {denied.off
                      ? `${denied.denied} isn’t switched on in this workspace.`
                      : `You don’t have access to ${denied.denied}. Ask an admin if you need it.`}
                  </InlineStatus>
                </div>
              )}
              {/* Keyed on the path: a screen that throws is replaced by the
                  fallback with the shell still around it, and the next screen
                  starts clean (SH-12). */}
              <ErrorBoundary
                key={location.pathname}
                fallback={<ScreenErrorFallback screen={screen} screenLabel={screenLabel(screen)} />}
              >
                <DeniedContext.Provider value={setDenied}>
                  <Routes>
                    <Route path="/" element={<Navigate to={homeFor(perms)} replace />} />
                    <Route path="/me" element={<MeScreen />} />
                    {/* Account-level, not gated by a module — like /me. */}
                    <Route path="/security" element={<SecurityScreen />} />
                    <Route path="/whats-new" element={<WhatsNewScreen />} />
                    <Route
                      path="/dashboard"
                      element={
                        <Guard perms={perms} module="dashboard">
                          <DashboardScreen />
                        </Guard>
                      }
                    />
                    <Route
                      path="/projects"
                      element={
                        <Guard perms={perms} module="projects">
                          <ProjectsScreen />
                        </Guard>
                      }
                    />
                    <Route
                      path="/project"
                      element={
                        <Guard perms={perms} module="projects">
                          <Suspense fallback={<RouteSpinner />}>
                            <ProjectDetailScreen />
                          </Suspense>
                        </Guard>
                      }
                    />
                    <Route
                      path="/profitability"
                      element={
                        <Guard perms={perms} module="projects" also={seesMoney}>
                          <Suspense fallback={<RouteSpinner />}>
                            <ProfitabilityScreen />
                          </Suspense>
                        </Guard>
                      }
                    />
                    <Route
                      path="/talent"
                      element={
                        <Guard perms={perms} module="talent">
                          <TalentScreen />
                        </Guard>
                      }
                    />
                    <Route
                      path="/capacity"
                      element={
                        <Guard perms={perms} module="capacity">
                          <CapacityScreen />
                        </Guard>
                      }
                    />
                    <Route
                      path="/timesheets"
                      element={
                        <Guard perms={perms} module="timesheets">
                          <TimesheetsScreen />
                        </Guard>
                      }
                    />
                    <Route
                      path="/gantt"
                      element={
                        <Guard perms={perms} module="schedule">
                          <GanttScreen />
                        </Guard>
                      }
                    />
                    <Route
                      path="/hr"
                      element={
                        <Guard perms={perms} module="people">
                          <PeopleHRScreen />
                        </Guard>
                      }
                    />
                    <Route
                      path="/reports"
                      element={
                        <Guard perms={perms} module="reports">
                          <ReportsScreen />
                        </Guard>
                      }
                    />
                    <Route
                      path="/org"
                      element={
                        <Guard perms={perms} module="org">
                          <OrgSettingsScreen />
                        </Guard>
                      }
                    />
                    <Route
                      path="/workspace"
                      element={
                        <Guard perms={perms} module={WORKSPACE_MODULES}>
                          <WorkspaceScreen attention={attention} />
                        </Guard>
                      }
                    />
                    {/* Inbox became Workspace → Notifications; keep old links working. */}
                    <Route
                      path="/inbox"
                      element={<Navigate to="/workspace?tab=notifications" replace />}
                    />
                    {Object.keys(PLACEHOLDERS).map((key) => (
                      <Route
                        key={key}
                        path={`/${key}`}
                        element={
                          <Guard perms={perms} module={ROUTE_MODULE[key] ?? key}>
                            <Placeholder {...PLACEHOLDERS[key]} />
                          </Guard>
                        }
                      />
                    ))}
                    <Route path="*" element={<Navigate to={homeFor(perms)} replace />} />
                  </Routes>
                </DeniedContext.Provider>
              </ErrorBoundary>
            </main>
            {/* The phone's day clock: at the foot of the screen, where a thumb
                is, not in the topbar's corner. A row under <main> rather than an
                overlay, so no page content ever sits beneath it; its foot clears
                the home indicator. The pill fills the row (tokens.css). */}
            {phone && clock && (
              <section
                aria-label="Day clock"
                className="hf-actionbar"
                style={{
                  flexShrink: 0,
                  display: 'flex',
                  // The day clock and a running project timer share the row; at
                  // 320 they take one each.
                  flexWrap: 'wrap',
                  gap: 'var(--hf-space-2)',
                  padding:
                    'var(--hf-space-2) var(--hf-space-4) max(var(--hf-space-2), env(safe-area-inset-bottom))',
                  borderTop: '1px solid var(--hf-rule)',
                  background: 'var(--hf-glass)',
                  backdropFilter: 'blur(20px)',
                }}
              >
                {clock}
              </section>
            )}
          </PageCrumbsProvider>
        </div>
      </div>
    </div>
  );
}
